import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useNavigate } from 'react-router-dom';
import {
  Bin,
  DuplicateIcon,
  EditIcon,
  IOption,
  Export,
  Card as ItemCard,
  MenuIcon,
  ViewIcon,
  SnackbarStatus,
  useSnackbar,
  Prompt,
} from '@weareopenr/component-library';
import { deleteMenu } from 'src/api/deleteMenu/deleteMenu';
import { boldMenuName } from 'src/helpers/truncateMenuName/boldMenuName';
import PublishModal from '../PublishModal';
import MenuModal from '../MenuModal';

export interface MenuCardProps {
  menuId: string;
  name: string;
  image?: string;
  consumerName?: string | null;
}

const MenuCard = ({ menuId, name, image, consumerName }: MenuCardProps) => {
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const [variant, setVariant] = useState<'update' | 'duplicate'>('update');
  const [isMenuModalVisible, setIsMenuModalVisible] = useState<boolean>(false);
  const [isPublishModalVisible, setIsPublishModalVisible] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const removePromptTitle = `Are you sure you want to delete "${name}"?`;
  const errorMessage = 'We have been unable to complete your request. Please try again';
  const navigate = useNavigate();
  const handleMenuModalToggle = (value: 'duplicate' | 'update'): void => {
    setVariant(value);
    setIsMenuModalVisible((prev) => !prev);
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await deleteMenu(menuId);
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: <>You have successfully deleted &quot;{boldMenuName(name)}&quot; menu.</>,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menus] });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: errorMessage,
      });
      setIsRemovePromptVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const itemEllipsisMenu: IOption[] = [
    {
      id: 0,
      name: 'View',
      icon: ViewIcon,
      onOptionClick: () => {
        navigate(`/menus/${menuId}`);
      },
    },
    {
      id: 1,
      name: 'Edit',
      icon: EditIcon,
      onOptionClick: () => {
        handleMenuModalToggle('update');
      },
    },
    {
      id: 2,
      name: 'Publish',
      icon: Export,
      onOptionClick: () => {
        setIsPublishModalVisible(true);
      },
    },
    {
      id: 3,
      name: 'Duplicate',
      icon: DuplicateIcon,
      onOptionClick: () => {
        handleMenuModalToggle('duplicate');
      },
    },
    { id: 'd-1', isDivider: true },
    {
      id: 4,
      name: 'Delete',
      icon: Bin,
      hoverColour: 'tomato',
      onOptionClick: () => {
        setIsRemovePromptVisible(true);
      },
      dataTestId: 'open-remove-prompt',
    },
  ];

  return (
    <div data-testid="card">
      <MenuModal
        title={`${variant === 'update' ? 'Edit' : 'Duplicate'} menu`}
        menuName={name}
        closeModal={() => setIsMenuModalVisible(false)}
        open={isMenuModalVisible}
        variant={variant}
        id={menuId}
        consumerName={consumerName}
        image={image}
      />
      <PublishModal
        isOpen={isPublishModalVisible}
        closeModal={() => setIsPublishModalVisible(false)}
        menuId={menuId}
        menuName={name}
      />
      <Prompt
        promptHeading={removePromptTitle}
        closePrompt={() => setIsRemovePromptVisible(false)}
        open={isRemovePromptVisible}
        buttonHandler={handleDelete}
        buttonText="Delete"
        buttonColour="tomato"
        backButton={true}
        size="small"
        isSubmitting={isSubmitting}
      >
        You will lose all the connections made with this menu.
      </Prompt>
      <ItemCard
        text={name}
        subtext={consumerName}
        key={menuId}
        imageSrc={image}
        to={`/menus/${menuId}`}
        ellipsisOptions={itemEllipsisMenu}
        icon={MenuIcon}
        variant="tertiary"
      />
    </div>
  );
};

export default MenuCard;
